<template>
  <div class="widget w-quote-detail-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header printView :wid="wid" marketDepthManager>
      <c-incremental-search
        slot="left"
        :ric="ric"
        @error="onSymbolSearchError"
        @change-ric="onSetRIC"
        :instrumentTypes="['STO', 'FUN', 'BON', 'WAR', 'CER', 'IND']"
      />
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <div v-if="ric" class="widget-body">
      <div class="panel">
        <v-button
          class="panel-header"
          :class="orderPanelOpen ? 'collapsed' : ''"
          :aria-expanded="orderPanelOpen ? 'true' : 'false'"
          aria-controls="collapse-order-panel"
          @click="onUpdatePanel('order')"
          ref="orderEntryPanel"
        >
          <div class="d-flex align-items-center panel-bar">
            <font-awesome-icon :icon="['far', `chevron-${orderPanelOpen ? 'down' : 'right'}`]" />
            <span class="panel-label">{{ i18n('orderEntry') }}</span>
            <span class="sr-only">{{ i18n('header') }}</span>
          </div>
        </v-button>
        <b-collapse :visible="orderPanelOpen" id="collapse-order-panel">
          <div>
            <w-order-entry-widget embedded noHeader ref="orderEntryWidget" :wid="wid" />
          </div>
        </b-collapse>
      </div>

      <div class="panel">
        <v-button
          class="panel-header"
          :class="quotePanelOpen ? 'collapsed' : ''"
          :aria-expanded="quotePanelOpen ? 'true' : 'false'"
          aria-controls="collapse-quote-panel"
          @click="onUpdatePanel('quote')"
        >
          <div class="d-flex align-items-center panel-bar">
            <font-awesome-icon :icon="['far', `chevron-${quotePanelOpen ? 'down' : 'right'}`]" />
            <span class="panel-label">{{ i18n('quote') }}</span>
            <span class="sr-only">{{ i18n('header') }}</span>
          </div>
        </v-button>
        <b-collapse :visible="quotePanelOpen" id="collapse-quote-panel">
          <div class="w-quote-widget">
            <quote-body
              v-if="quotePanelOpen"
              class="border-left-0 border-right-0"
              :wid="wid"
              :ric="ric"
              :buyAction="() => onBuyClicked()"
              :sellAction="() => onSellClicked()"
              :coverAction="onCoverClicked"
              :shortAction="onShortClicked"
              embedded
            />
          </div>
        </b-collapse>
      </div>

      <div class="panel">
        <v-button
          class="panel-header"
          :class="marketDepthPanelOpen ? 'collapsed' : ''"
          :aria-expanded="marketDepthPanelOpen ? 'true' : 'false'"
          aria-controls="collapse-market-depth-panel"
          @click="onUpdatePanel('market-Depth')"
        >
          <div class="d-flex align-items-center panel-bar">
            <font-awesome-icon :icon="['far', `chevron-${marketDepthPanelOpen ? 'down' : 'right'}`]" />
            <span class="panel-label">{{ i18n('marketDepth') }}</span>
            <span class="sr-only">{{ i18n('header') }}</span>
          </div>
        </v-button>
        <b-collapse :visible="marketDepthPanelOpen" id="collapse-market-depth-panel">
          <div class="w-market-depth-l2-widget">
            <market-depth-control-bar :ric="ric" :view="marketDepthView" @update-view="onUpdateView" />
            <market-depth-body
              v-if="marketDepthPanelOpen"
              class="border-left-0 border-right-0"
              :wid="wid"
              :ric="ric"
              :view="marketDepthView"
              :buyAction="onBuyClicked"
              :sellAction="onSellClicked"
              embedded
            />
          </div>
        </b-collapse>
      </div>

      <div class="panel">
        <v-button
          class="panel-header"
          :class="newsPanelOpen ? 'collapsed' : ''"
          :aria-expanded="newsPanelOpen ? 'true' : 'false'"
          aria-controls="collapse-news-panel"
          @click="onUpdatePanel('news')"
        >
          <div class="d-flex align-items-center panel-bar">
            <font-awesome-icon :icon="['far', `chevron-${newsPanelOpen ? 'down' : 'right'}`]" />
            <span class="panel-label">{{ i18n('news') }}</span>
            <span class="sr-only">{{ i18n('header') }}</span>
          </div>
        </v-button>
        <b-collapse :visible="newsPanelOpen" id="collapse-news-panel">
          <div class="w-news-widget">
            <w-news-widget v-if="newsPanelOpen" :wid="wid" embeded :externalSymbol="[ric]" />
          </div>
        </b-collapse>
      </div>
    </div>
    <v-alert v-else align="center" class="m-2" show variant="light">{{ i18n('nosymbol', 'globalMessages') }}</v-alert>
    <b-toaster :name="notifierId"></b-toaster>
  </div>
</template>

<script>
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CIncrementalSearch from '@custom/CIncrementalSearch';
import QuoteBody from '@widgets/WQuoteWidget/WBody';
import MarketDepthControlBar from '@widgets/WMarketDepthL2Widget/WControlBar';
import MarketDepthBody from '@widgets/WMarketDepthL2Widget/WBody';
import WNewsWidget from '@widgets/WNewsWidget/WNewsWidget';

import WOrderEntryWidget from '@widgets/WOrderEntryWidget/WOrderEntryWidget';

import widgetCommons from '@mixins/widgetCommons';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown as farChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { toSymbol } from '@state/api';

library.add(farChevronDown);

export default {
  name: 'w-quote-detail-widget',
  mixins: [widgetCommons],
  components: {
    CWidgetHeader,
    CWidgetControlBar,
    CIncrementalSearch,
    FontAwesomeIcon,
    QuoteBody,
    MarketDepthControlBar,
    MarketDepthBody,
    WNewsWidget,
    WOrderEntryWidget
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  computed: {
    ric() {
      return this.$store.state.widget[this.wid].ric;
    },
    orderPanelOpen() {
      return this.$store.state.widget[this.wid].orderPanelOpen;
    },
    quotePanelOpen() {
      return this.$store.state.widget[this.wid].quotePanelOpen;
    },
    marketDepthPanelOpen() {
      return this.$store.state.widget[this.wid].marketDepthPanelOpen;
    },
    newsPanelOpen() {
      return this.$store.state.widget[this.wid].newsPanelOpen;
    },
    // MarketDepth
    marketDepthView() {
      return this.$store.state.widget[this.wid].marketDepth.view;
    }
  },
  methods: {
    onSetRIC(value) {
      if (this.ric !== value) {
        this.$store.commit(`widget/${this.wid}/RIC_CHANGED`, value || '');
        this.sendLinkUpdate(value);
      }
    },
    onUpdatePanel(panel) {
      const mutation = panel.replace(/-/g, '_').toUpperCase();
      const panelState = this[`${panel.replace(/-/g, '')}PanelOpen`];
      this.$store.commit(`widget/${this.wid}/${mutation}_PANEL_CHANGED`, !panelState);
    },
    onBuyClicked(mdPrice) {
      this.handleClick('BUY', mdPrice);
    },
    onSellClicked(mdPrice) {
      this.handleClick('SELL', mdPrice);
    },
    onCoverClicked() {
      this.handleClick('COVER');
    },
    onShortClicked() {
      this.handleClick('SHORT');
    },
    handleClick(action, mdPrice) {
      let modifier,
        limit,
        reregister = false;
      if (!isNaN(mdPrice)) {
        modifier = 'limit';
        limit = Number.parseFloat(mdPrice);
      }

      toSymbol(this.ric).then(tickers => {
        const currency = tickers[0].split(':')[1] === 'CA' ? 'CAD' : 'USD';

        //select appropriate account
        const isShortAction = ['COVER', 'SHORT'].includes(action);
        const currentAccount = this.$store.getters['accounts/getAccount'](this.$store.state.widget[this.wid].accountId);
        if (
          (currentAccount.type.acctType === 'MARGIN_SHORT' && !isShortAction) ||
          (currentAccount.type.acctType !== 'MARGIN_SHORT' && isShortAction)
        ) {
          const defaultOrderEntryAccount = this.$store.getters['accounts/getDefaultOrderEntryAccount'](
            isShortAction,
            isShortAction ? currency : undefined //not doing currency match for Non-Short accounts
          );

          this.$store.commit(`widget/${this.wid}/SET_ACCOUNT_ID`, defaultOrderEntryAccount.id);
        }

        //Convert the embedded OE panel to stock
        this.$store.commit(`widget/${this.wid}/REMOVE_STRATEGY`);
        const ticketType = this.$store.state.widget[this.wid].legs['firstOrder'].ticketType;
        if (ticketType !== 'stock') {
          reregister = true;

          this.$store.commit(`widget/${this.wid}/SET_LEG_VALUE`, {
            leg: 'firstOrder',
            key: 'ticketType',
            value: 'stock'
          });
        }

        //Set the action
        this.$store.commit(`widget/${this.wid}/CLEAR_ORDER_MODE`); //To make the action reactive, clearing the order mode first
        this.$store.commit(`widget/${this.wid}/SET_ORDER_MODE`, { action, modifier, limit, reregister });
        if (!this.orderPanelOpen) {
          this.$store.commit(`widget/${this.wid}/ORDER_PANEL_CHANGED`, true);
        }
      });
      this.$refs.orderEntryPanel.focus();
    },

    // MarketDepthL2
    onUpdateView(view) {
      this.$store.commit('widget/' + this.wid + '/VIEW_CHANGED', view);
    }
  },
  watch: {
    ric: {
      handler(value) {
        this.$store.commit(`widget/${this.wid}/SET_RIC`, value);
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/quote-detail-widget';
@import '~@/styles/widgets/quote-widget';
@import '~@/styles/widgets/market-depth-l2-widget';
@import '~@/styles/widgets/news-widget';
</style>
